import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reset scroll to (0, 0) whenever the route changes.
 *
 * React Router doesn't do this by default - switching pages keeps the prior
 * scroll position, which usually feels broken (long news article → click a
 * link → you land halfway down the next page).
 *
 * We skip the reset when the URL has a `#hash` so anchor navigation still
 * works (e.g. /about#safety).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Disable browser scroll restoration so it doesn't fight us.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (hash) {
      // Wait one tick so the destination element is mounted before scrolling.
      const id = hash.replace("#", "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ block: "start" });
      });
      return;
    }

    // Two ticks: one for React to paint the new route, another for any
    // late-mounting layout (lazy images, async data) that bumps the height.
    window.scrollTo(0, 0);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    });
  }, [pathname, hash]);

  return null;
}

